import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { engagedMembersService, EngagedMembersData, EngagedMember } from '../../services/engagedMembersService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

const SECTION_COLORS: Record<string, string> = {
  VCN: '#3b82f6',
  HDR: '#8b5cf6',
  SANTE: '#10b981',
};

export const AdminEngagedMembers: React.FC = () => {
  const [data, setData] = useState<EngagedMembersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'likes' | 'comments' | 'name'>('score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedMember, setSelectedMember] = useState<EngagedMember | null>(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const membersPerPage = 20;

  useEffect(() => {
    fetchEngagedMembers();
    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(fetchEngagedMembers, 60000);
    return () => clearInterval(interval);
  }, [period]);

  // Réinitialiser la page lors du changement de filtre
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, sortOrder, period]);

  const fetchEngagedMembers = async () => {
    try {
      setLoading(true);
      const membersData = await engagedMembersService.getEngagedMembers(period);
      setData(membersData);
    } catch (error) {
      console.error('Erreur lors de la récupération des membres engagés:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout activeSection="engaged-members">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  // Filtrer et trier les membres
  const filteredAndSortedMembers = [...data.members]
    .filter((member) => {
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      const search = searchQuery.toLowerCase();
      return (
        fullName.includes(search) ||
        member.username.toLowerCase().includes(search) ||
        member.email.toLowerCase().includes(search) ||
        member.section.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'score':
          aValue = a.stats.total_score;
          bValue = b.stats.total_score;
          break;
        case 'likes':
          aValue = a.stats.total_likes;
          bValue = b.stats.total_likes;
          break;
        case 'comments':
          aValue = a.stats.total_comments;
          bValue = b.stats.total_comments;
          break;
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });

  // Préparer les données pour les graphiques
  const topMembersData = filteredAndSortedMembers.slice(0, 10).map((member, index) => ({
    name: `${member.first_name} ${member.last_name}`.substring(0, 15),
    score: member.stats.total_score,
    likes: member.stats.total_likes,
    comments: member.stats.total_comments,
    rank: index + 1,
  }));

  const sectionStatsData = Object.entries(data.section_stats).map(([section, stats]) => ({
    name: section,
    members: stats.total_members,
    score: stats.total_score,
    likes: stats.total_likes,
    comments: stats.total_comments,
  }));

  const likesByTypeData = filteredAndSortedMembers.reduce(
    (acc, member) => {
      acc.expose += member.stats.likes_by_type.expose;
      acc.ramadan += member.stats.likes_by_type.ramadan;
      acc.event += member.stats.likes_by_type.event;
      acc.audio += member.stats.likes_by_type.audio;
      acc.daara += member.stats.likes_by_type.daara;
      return acc;
    },
    { expose: 0, ramadan: 0, event: 0, audio: 0, daara: 0 }
  );

  const likesByTypeChartData = [
    { name: 'Exposés', value: likesByTypeData.expose },
    { name: 'Ramadan', value: likesByTypeData.ramadan },
    { name: 'Événements', value: likesByTypeData.event },
    { name: 'Audios', value: likesByTypeData.audio },
    { name: 'Daara', value: likesByTypeData.daara },
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPeriodLabel = (p: string) => {
    switch (p) {
      case '7d':
        return '7 derniers jours';
      case '30d':
        return '30 derniers jours';
      case '90d':
        return '90 derniers jours';
      case 'all':
        return 'Tout le temps';
      default:
        return p;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedMembers.length / membersPerPage);
  const startIndex = (currentPage - 1) * membersPerPage;
  const endIndex = startIndex + membersPerPage;
  const paginatedMembers = filteredAndSortedMembers.slice(startIndex, endIndex);

  // Export CSV
  const handleExportCSV = () => {
    const headers = ['Rang', 'Nom', 'Prénom', 'Username', 'Email', 'Section', 'Score Total', 'Likes', 'Commentaires', 'Date Inscription', 'Dernière Activité'];
    const rows = filteredAndSortedMembers.map((member, index) => [
      index + 1,
      member.last_name,
      member.first_name,
      member.username,
      member.email,
      member.section,
      member.stats.total_score,
      member.stats.total_likes,
      member.stats.total_comments,
      formatDate(member.date_joined),
      formatDate(member.last_activity),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `membres-engages-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Ouvrir modal de détails
  const handleViewMemberDetails = (member: EngagedMember) => {
    setSelectedMember(member);
    setShowMemberModal(true);
  };

  return (
    <AdminLayout activeSection="engaged-members">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-green-700">Membres Engagés</h2>
            <p className="text-gray-600 mt-1">Statistiques détaillées des membres les plus actifs</p>
          </div>
          <button
            onClick={fetchEngagedMembers}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <span>🔄</span>
            <span>Actualiser</span>
          </button>
        </div>

        {/* Filtres par période */}
        <div className="mt-4 flex flex-wrap gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                period === p
                  ? 'bg-green-600 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-green-50 border border-gray-300'
              }`}
            >
              {formatPeriodLabel(p)}
            </button>
          ))}
        </div>
      </div>

      {/* Cartes de résumé */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm mb-1">Total Membres</p>
              <p className="text-3xl font-bold">{data.summary.total_members}</p>
            </div>
            <div className="text-4xl opacity-80">👥</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Total Likes</p>
              <p className="text-3xl font-bold">{data.summary.total_likes}</p>
              <p className="text-xs text-blue-100 mt-1">❤️ {data.summary.total_likes}</p>
            </div>
            <div className="text-4xl opacity-80">❤️</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm mb-1">Total Commentaires</p>
              <p className="text-3xl font-bold">{data.summary.total_comments}</p>
              <p className="text-xs text-purple-100 mt-1">💬 {data.summary.total_comments}</p>
            </div>
            <div className="text-4xl opacity-80">💬</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm mb-1">Score Moyen</p>
              <p className="text-3xl font-bold">{data.summary.average_score}</p>
              <p className="text-xs text-amber-100 mt-1">Score total: {data.summary.total_score}</p>
            </div>
            <div className="text-4xl opacity-80">⭐</div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top 10 Membres par Score */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Top 10 Membres par Score</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topMembersData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="score" fill="#10b981" name="Score Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Statistiques par Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Statistiques par Section</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sectionStatsData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.name}: ${entry.score}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="score"
              >
                {sectionStatsData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={SECTION_COLORS[entry.name] || COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Likes par Type */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Likes par Type de Contenu</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={likesByTypeChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.name}: ${entry.value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {likesByTypeChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Comparaison Likes/Commentaires */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Likes vs Commentaires (Top 10)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topMembersData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="likes" fill="#3b82f6" name="Likes" />
              <Bar dataKey="comments" fill="#8b5cf6" name="Commentaires" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tableau des membres */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="text-xl font-bold text-gray-800">
            Liste des Membres ({filteredAndSortedMembers.length})
          </h3>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="🔍 Rechercher un membre..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="score">Score</option>
                <option value="likes">Likes</option>
                <option value="comments">Commentaires</option>
                <option value="name">Nom</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                title="Exporter en CSV"
              >
                <span>📥</span>
                <span className="hidden sm:inline">Exporter CSV</span>
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Rang</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Membre</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Section</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Score</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Likes</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Commentaires</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Dernière Activité</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.map((member, index) => {
                const globalIndex = startIndex + index;
                return (
                <tr
                  key={member.user_id}
                  className="border-b border-gray-100 hover:bg-green-50 transition-colors cursor-pointer"
                  onClick={() => handleViewMemberDetails(member)}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      {globalIndex < 3 ? (
                        <span className="text-2xl">
                          {globalIndex === 0 ? '🥇' : globalIndex === 1 ? '🥈' : '🥉'}
                        </span>
                      ) : (
                        <span className="font-semibold text-gray-600">#{globalIndex + 1}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      {member.photo ? (
                        <img
                          src={member.photo}
                          alt={`${member.first_name} ${member.last_name}`}
                          className="w-10 h-10 rounded-full object-cover border-2 border-green-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold border-2 border-green-200">
                          {member.first_name?.[0] || member.username[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-gray-800">
                          {member.first_name} {member.last_name}
                        </div>
                        <div className="text-sm text-gray-500">@{member.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        member.section === 'VCN'
                          ? 'bg-blue-100 text-blue-800'
                          : member.section === 'HDR'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {member.section}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-bold text-green-700 text-lg">{member.stats.total_score}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-blue-600 font-semibold">❤️ {member.stats.total_likes}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-purple-600 font-semibold">💬 {member.stats.total_comments}</span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-600">
                    {formatDate(member.last_activity)}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>

          {filteredAndSortedMembers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">Aucun membre trouvé</p>
              <p className="text-sm mt-2">Essayez de modifier vos critères de recherche</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Précédent
            </button>
            <span className="px-4 py-2 text-gray-700">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Suivant →
            </button>
          </div>
        )}
      </div>

      {/* Modal de détails du membre */}
      {showMemberModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-4">
                  {selectedMember.photo ? (
                    <img
                      src={selectedMember.photo}
                      alt={`${selectedMember.first_name} ${selectedMember.last_name}`}
                      className="w-16 h-16 rounded-full object-cover border-4 border-green-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xl border-4 border-green-200">
                      {selectedMember.first_name?.[0] || selectedMember.username[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {selectedMember.first_name} {selectedMember.last_name}
                    </h3>
                    <p className="text-gray-600">@{selectedMember.username}</p>
                    <span
                      className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        selectedMember.section === 'VCN'
                          ? 'bg-blue-100 text-blue-800'
                          : selectedMember.section === 'HDR'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {selectedMember.section}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowMemberModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Statistiques principales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <p className="text-green-100 text-sm mb-1">Score Total</p>
                  <p className="text-3xl font-bold">{selectedMember.stats.total_score}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <p className="text-blue-100 text-sm mb-1">Total Likes</p>
                  <p className="text-3xl font-bold">{selectedMember.stats.total_likes}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                  <p className="text-purple-100 text-sm mb-1">Total Commentaires</p>
                  <p className="text-3xl font-bold">{selectedMember.stats.total_comments}</p>
                </div>
              </div>

              {/* Likes par type */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Likes par Type de Contenu</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📝</p>
                    <p className="text-sm text-gray-600 mb-1">Exposés</p>
                    <p className="text-xl font-bold text-blue-600">{selectedMember.stats.likes_by_type.expose}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">🌙</p>
                    <p className="text-sm text-gray-600 mb-1">Ramadan</p>
                    <p className="text-xl font-bold text-purple-600">{selectedMember.stats.likes_by_type.ramadan}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📅</p>
                    <p className="text-sm text-gray-600 mb-1">Événements</p>
                    <p className="text-xl font-bold text-amber-600">{selectedMember.stats.likes_by_type.event}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">🎵</p>
                    <p className="text-sm text-gray-600 mb-1">Audios</p>
                    <p className="text-xl font-bold text-indigo-600">{selectedMember.stats.likes_by_type.audio}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📖</p>
                    <p className="text-sm text-gray-600 mb-1">Daara</p>
                    <p className="text-xl font-bold text-teal-600">{selectedMember.stats.likes_by_type.daara}</p>
                  </div>
                </div>
              </div>

              {/* Commentaires par type */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Commentaires par Type de Contenu</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📝</p>
                    <p className="text-sm text-gray-600 mb-1">Exposés</p>
                    <p className="text-xl font-bold text-blue-600">{selectedMember.stats.comments_by_type.expose}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">🌙</p>
                    <p className="text-sm text-gray-600 mb-1">Ramadan</p>
                    <p className="text-xl font-bold text-purple-600">{selectedMember.stats.comments_by_type.ramadan}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📅</p>
                    <p className="text-sm text-gray-600 mb-1">Événements</p>
                    <p className="text-xl font-bold text-amber-600">{selectedMember.stats.comments_by_type.event}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-2">📖</p>
                    <p className="text-sm text-gray-600 mb-1">Daara</p>
                    <p className="text-xl font-bold text-teal-600">{selectedMember.stats.comments_by_type.daara}</p>
                  </div>
                </div>
              </div>

              {/* Informations additionnelles */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-bold text-gray-800 mb-3">Informations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-semibold text-gray-800">{selectedMember.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Date d'inscription</p>
                    <p className="font-semibold text-gray-800">{formatDate(selectedMember.date_joined)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Dernière activité</p>
                    <p className="font-semibold text-gray-800">{formatDate(selectedMember.last_activity) || 'Aucune activité récente'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Section</p>
                    <p className="font-semibold text-gray-800">{selectedMember.section}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

